// The filter panel both boards render.
//
// The pipeline Kanban and the Listings tab share this component, so a filter added to
// filterSpec.js appears on both at once, styled the same, sending the same query string to the
// same backend builder. Each page still owns what only it has — the pipeline its saved
// searches, the Listings tab its status/type selectors — which it passes in as `leading`.
//
// WHY IT COLLAPSES
// There are ~25 filters. Rendered flat in the pipeline's 4-column grid they run to seven rows
// and push the Kanban columns off the first screen, which is how a filter bar becomes
// something people scroll past. So a short primary row is always visible and the rest sits
// behind "More filters", with a count badge and removable chips so nothing set is ever hidden
// from view — an invisible active filter is worse than a tall panel.

import { Fragment, useState } from "react";
import FilterControl from "./controls";
import {
  FILTERS, GROUPS, activeCount, isSet, keysFor,
} from "./filterSpec";
import {
  panelStyle, gridStyle, groupHeadingStyle, primaryButton, secondaryButton,
  toggleButton, countBadge, activeChipStyle,
} from "./styles";

// Shown before the panel is expanded: the handful people reach for every time.
const PRIMARY_KEYS = ["price", "beds", "baths", "sqft", "reno", "dom"];

/** Human-readable summary of one set filter, for the active chips. */
function describe(f, values) {
  const label = f.label.replace(/\s*\(.*\)$/, "");
  if (f.control === "chips") {
    const chosen = (values[f.key] || [])
      .map((v) => f.options.find((o) => o.value === v)?.label || v);
    return `${label}: ${chosen.join(", ")}`;
  }
  if (f.control === "select") {
    return `${label}: ${f.options.find((o) => o.value === values[f.key])?.label || values[f.key]}`;
  }
  if (f.control === "days") return `${label}: within ${values[f.key]}d`;
  const [minKey, maxKey] = keysFor(f);
  const lo = values[minKey];
  const hi = values[maxKey];
  const fmt = (v) => (f.choices ? f.choices.find((c) => String(c.value) === String(v))?.label || v
                                : Number(v).toLocaleString());
  if (isSet(lo) && isSet(hi)) return `${label}: ${fmt(lo)}–${fmt(hi)}`;
  if (isSet(lo)) return `${label}: ≥ ${fmt(lo)}`;
  return `${label}: ≤ ${fmt(hi)}`;
}

export default function ListingFilterPanel({
  values,
  set,
  onSearch,
  onReset,
  loading = false,
  leading = null,      // page-specific controls rendered first inside the grid
  trailing = null,     // page-specific controls rendered after the filters
  header = null,       // e.g. the pipeline's SavedSearchBar
  actions = null,      // page-specific buttons in the Search/Reset/More-filters row
  scope = "pipeline",
}) {
  const [expanded, setExpanded] = useState(false);

  const visible = FILTERS.filter((f) => !f.scopes || f.scopes.includes(scope));
  const primary = visible.filter((f) => PRIMARY_KEYS.includes(f.key));
  const n = activeCount(values);

  // Everything set but not shown in the collapsed row — so a filter can never be silently on.
  const hiddenActive = expanded
    ? []
    : visible.filter((f) => !PRIMARY_KEYS.includes(f.key) && keysFor(f).some((k) => isSet(values[k])));

  const clearFilter = (f) => {
    for (const k of keysFor(f)) set(k, f.control === "chips" ? [] : "");
  };

  const onEnter = (e) => { if (e.key === "Enter") onSearch(); };

  return (
    <div style={panelStyle} onKeyDown={onEnter}>
      {header}

      <div style={gridStyle}>
        {leading}
        {(expanded ? visible : primary).map((f, i) => {
          // In the expanded view each category gets a heading; collapsed shows no headings.
          const prev = expanded ? (visible[i - 1] || {}).group : null;
          const heading = expanded && f.group !== prev
            ? <div key={`h-${f.group}`} style={groupHeadingStyle}>
                {GROUPS.find((g) => g.key === f.group)?.label || f.group}
              </div>
            : null;
          return (
            <Fragment key={f.key}>
              {heading}
              <FilterControl f={f} values={values} set={set} />
            </Fragment>
          );
        })}
        {trailing}
      </div>

      {hiddenActive.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "12px" }}>
          {hiddenActive.map((f) => (
            <span key={f.key} style={activeChipStyle}>
              {describe(f, values)}
              <span role="button" tabIndex={0} title="Remove this filter"
                onClick={() => clearFilter(f)}
                onKeyDown={(e) => { if (e.key === "Enter") clearFilter(f); }}
                style={{ cursor: "pointer", fontWeight: 700, color: "#5b7fa6" }}>×</span>
            </span>
          ))}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "14px" }}>
        <button onClick={onSearch} disabled={loading} style={primaryButton(loading)}>
          {loading ? "Loading…" : "Search"}
        </button>
        <button onClick={onReset} style={secondaryButton}>Reset</button>
        <button onClick={() => setExpanded((v) => !v)} style={toggleButton}>
          {expanded ? "Fewer filters ▴" : "More filters ▾"}
          {n > 0 && <span style={countBadge}>{n} active</span>}
        </button>
        {/* Rendered in the button ROW rather than the filter grid, so a page-specific
            action lines up with More filters instead of sitting among the controls.
            Use `toggleButton` for it and the heights match by construction. */}
        {actions}
      </div>
    </div>
  );
}
